import React from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, BOOKING_SCREENING_URL, PHONE_NUMBER, PHONE_TEL, EMAIL_ADDRESS } from '../lib/tracking';
import { optimizeImage } from '../lib/image';

export default function Footer() {
  return (
    <footer className="bg-[#0F141C] text-[#F1F4F8] pt-16 pb-24 min-[1200px]:pb-16 border-t border-[#5A232E]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-12 border-b border-[#1B222C]">
          {/* Col 1: Brand & Bio */}
          <div className="lg:col-span-4 space-y-4">
            <div className="flex items-center gap-3">
              <img
                src={optimizeImage(
                  'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/logo-fra-robert.png',
                  240
                )}
                alt="Psykoterapeut Kim Palasz Logo"
                className="h-20 sm:h-24 w-auto object-contain bg-white/5 rounded-2xl p-2 border border-white/10"
              />
            </div>

            <p className="text-sm text-[#F1F4F8]/80 leading-relaxed max-w-sm">
              Nærværende og professionel terapi i trygge rammer på Amager. Speciale i traumeterapi, angst, stress og livskriser.
            </p>

            {/* MPF Badge & Socials */}
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <a
                href="https://psykoterapeutforeningen.dk/psykoterapeuter/93030/Kim-Palasz"
                target="_blank"
                rel="noopener noreferrer"
                title="Medlem af Dansk Psykoterapeutforening (MPF)"
                className="inline-block bg-white rounded-lg p-1.5 hover:opacity-95 transition-opacity"
              >
                <img
                  src={optimizeImage(
                    'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/jpg-black.webp',
                    120
                  )}
                  alt="Dansk Psykoterapeutforening MPF godkendt"
                  className="h-9 w-auto object-contain"
                />
              </a>

              <div className="flex items-center gap-2">
                <a
                  href="https://facebook.com/PsykoterapeutKimPalasz"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Besøg Facebook side"
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-[#5A232E] text-white flex items-center justify-center transition-colors"
                >
                  <i className="fa-brands fa-facebook-f" aria-hidden="true" />
                </a>
                <a
                  href="https://www.instagram.com/psykoterapeut_kim_palasz/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Besøg Instagram profil"
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-[#5A232E] text-white flex items-center justify-center transition-colors"
                >
                  <i className="fa-brands fa-instagram" aria-hidden="true" />
                </a>
                <a
                  href="https://www.youtube.com/@psykoterapeutkimpalasz"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Besøg YouTube kanal"
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-[#5A232E] text-white flex items-center justify-center transition-colors"
                >
                  <i className="fa-brands fa-youtube" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>

          {/* Col 2: Problemområder */}
          <div className="lg:col-span-4 space-y-3">
            <h4 className="font-display text-base text-white font-medium border-b border-white/10 pb-2">
              Få hjælp til
            </h4>
            <ul className="space-y-2 text-sm text-[#F1F4F8]/80">
              <li>
                <Link to="/stress/" className="hover:text-white transition-colors">Stressbehandling</Link>
              </li>
              <li>
                <Link to="/angst/" className="hover:text-white transition-colors">Angst og uro</Link>
              </li>
              <li>
                <Link to="/depression/" className="hover:text-white transition-colors">Depression & nedtrykthed</Link>
              </li>
              <li>
                <Link to="/lavt-selvvaerd/" className="hover:text-white transition-colors">Lavt selvværd & selvtillid</Link>
              </li>
              <li>
                <Link to="/traumer/" className="hover:text-white transition-colors">Traumeterapi (TBT)</Link>
              </li>
              <li>
                <Link to="/sorg-og-krise/" className="hover:text-white transition-colors">Sorg & livskriser</Link>
              </li>
              <li>
                <Link to="/hypnose-vaner/" className="hover:text-white transition-colors">Vaner: rygestop & sukkertrang</Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Kontaktinformation */}
          <div className="lg:col-span-4 space-y-3">
            <h4 className="font-display text-base text-white font-medium border-b border-white/10 pb-2">
              Kontakt & Praksis
            </h4>
            <div className="space-y-3 text-sm text-[#F1F4F8]/80 pt-1">
              <div className="flex items-start gap-2.5">
                <i className="fa-solid fa-location-dot text-emerald-400 mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <span className="text-white font-medium block">Psykoterapeut Kim Palasz</span>
                  <span>
                    Marengovej 2<br />
                    2300 København S
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <i className="fa-solid fa-phone text-emerald-400 shrink-0" aria-hidden="true" />
                <a
                  href={PHONE_TEL}
                  onClick={() => trackEvent('phone_click', { location: 'footer' })}
                  className="hover:text-white transition-colors no-break whitespace-nowrap font-medium"
                >
                  {PHONE_NUMBER}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <i className="fa-solid fa-envelope text-emerald-400 shrink-0" aria-hidden="true" />
                <a
                  href={`mailto:${EMAIL_ADDRESS}`}
                  onClick={() => trackEvent('email_click', { location: 'footer' })}
                  className="hover:text-white transition-colors no-break whitespace-nowrap"
                >
                  {EMAIL_ADDRESS}
                </a>
              </div>
              <div className="flex items-center gap-2.5 text-xs text-[#F1F4F8]/60 pt-1">
                <i className="fa-solid fa-building text-emerald-400/70 shrink-0" aria-hidden="true" />
                <span>CVR: 36149914</span>
              </div>
              <div className="pt-2">
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_booking_click', { location: 'footer_contact_col' })}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#5A232E] hover:bg-[#431722] text-[#F1F4F8] text-xs font-semibold tracking-wide transition-colors"
                >
                  <span>Book gratis samtale</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom copyright notice & legal links */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-[#F1F4F8]/60 gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <p>© {new Date().getFullYear()} Psykoterapeut Kim Palasz. CVR: 36149914. Alle rettigheder forbeholdes.</p>
            <span className="hidden sm:inline text-white/20">·</span>
            <Link to="/privatlivspolitik/" className="hover:text-white underline transition-colors">
              Privatlivspolitik
            </Link>
          </div>
          <p className="text-center sm:text-right text-[#F1F4F8]/40">
            Marengovej 2, 2300 København S · Tlf. {PHONE_NUMBER}
          </p>
        </div>
      </div>
    </footer>
  );
}
